/**
 * Route table. Landing (`/`) is the ONLY eager route — everything else is
 * React.lazy per the KICKOFF §3.1 budget (initial JS ≤180KB gz; route-split
 * everything). The test suite asserts this map stays complete.
 */
import { lazy, type ComponentType, type LazyExoticComponent } from "react";

export type RouteLoader = () => Promise<{ default: ComponentType }>;

/** Every lazily-loaded route path → its dynamic import. */
export const LAZY_ROUTE_LOADERS = {
  "/auth": () => import("./routes/Auth"),
  "/cabinet": () => import("./routes/Cabinet"),
  // LOOP 4 — the trust surfaces (BUILDER-var-player). ADD-only.
  "/confidence": () => import("./routes/Confidence"),
  // LOOP 4B — the tournament surfaces (BUILDER-events-player). ADD-only.
  "/events": () => import("./routes/Events"),
  "/events/:id": () => import("./routes/EventBracket"),
  "/feed": () => import("./routes/Feed"),
  // WHISTLE CHECK §4 — FIND ME A MATCH (the OPEN PLAY empty-feed secondary
  // flow: subscribe and wait instead of hosting). ADD-only.
  "/find-match": () => import("./routes/FindMatch"),
  "/first-night": () => import("./routes/FirstNight"),
  "/help": () => import("./routes/Help"),
  "/host": () => import("./routes/HostWizard"),
  // LOOP 5 — the voice surfaces (BUILDER-voice-player). ADD-only.
  "/inbox": () => import("./routes/Inbox"),
  "/juice-demo": () => import("./routes/JuiceDemo"),
  // HOME GROUND — the identity hub. Built in Loop 2 but never routed until the
  // AppChrome pass (24 Jul): the global nav drawer is the first thing that
  // ever linked here, so the gap stayed invisible until the drawer existed.
  "/locker": () => import("./routes/LockerRoom"),
  "/m/:id": () => import("./routes/MagicLink"),
  // LOOP 5 — the voice surfaces (BUILDER-voice-player). ADD-only.
  "/notifications/prefs": () => import("./routes/NotificationPrefs"),
  // LOOP 3 — the reward surfaces (BUILDER-moments-ui). ADD-only.
  "/night-recap": () => import("./routes/NightRecap"),
  // LOOP 5 — the Night Room (BUILDER-voice-player). ADD-only.
  "/room": () => import("./routes/Room"),
  "/share-relay": () => import("./routes/ShareRelay"),
  // THE STORE (25 Jul) — buy avatars with coins, post-onboarding. Fixes the
  // dead LockerRoom/AvatarRail navigate("/store") calls. ADD-only.
  "/store": () => import("./routes/Store"),
  // THE TABLE — the night's leaderboard (docs/briefs/THE-TABLE.md). ADD-only.
  "/table": () => import("./routes/Table"),
  "/terms": () => import("./routes/Terms"),
  "/vault": () => import("./routes/Vault"),
} satisfies Record<string, RouteLoader>;

export type LazyRoutePath = keyof typeof LAZY_ROUTE_LOADERS;

export const LAZY_ROUTES: ReadonlyArray<{
  path: LazyRoutePath;
  component: LazyExoticComponent<ComponentType>;
}> = (Object.entries(LAZY_ROUTE_LOADERS) as Array<[LazyRoutePath, RouteLoader]>).map(
  ([path, load]) => ({ path, component: lazy(load) }),
);
